import { useLabelLeadCounts } from "@/hooks/useLeadLabels";
import { LabelPicker, type LabelOption } from "@/components/labels/LabelPicker";
import { useCrmStore } from "@/stores/crm.store";

/**
 * Label filter for the Pipeline de Prospectos filter bar.
 *
 * Binds the shared `LabelPicker` to the crm store and to counts scoped by the
 * pipeline's OTHER active filters (never `labelIds` itself), so each option
 * predicts how many leads it would leave. The Buzón binds the same picker to its
 * own local state — see InboxLabelFilter.
 */
export function LabelFilterSelect(): React.JSX.Element | null {
  const labelIds = useCrmStore((s) => s.labelIds);
  const toggleLabelFilter = useCrmStore((s) => s.toggleLabelFilter);
  const clearLabelFilters = useCrmStore((s) => s.clearLabelFilters);

  // Other active filters shape the counts (never labelIds itself).
  const stages = useCrmStore((s) => s.stages);
  const channel = useCrmStore((s) => s.channel);
  const scoreMin = useCrmStore((s) => s.scoreMin);
  const scoreMax = useCrmStore((s) => s.scoreMax);
  const productIds = useCrmStore((s) => s.productIds);
  const cycleIds = useCrmStore((s) => s.cycleIds);
  const search = useCrmStore((s) => s.search);
  const from = useCrmStore((s) => s.from);
  const to = useCrmStore((s) => s.to);
  const assigneeIds = useCrmStore((s) => s.assigneeIds);

  const { data: countsData } = useLabelLeadCounts({
    stages: stages.join(","),
    channel,
    scoreMin,
    scoreMax,
    productIds: productIds.join(","),
    cycleIds: cycleIds.join(","),
    search,
    from,
    to,
    assigneeIds: assigneeIds.join(","),
  });

  const options: LabelOption[] = (countsData?.counts ?? []).map((c) => ({
    id: c.labelId,
    name: c.name,
    color: c.color,
    count: c.count,
  }));

  return (
    <LabelPicker
      options={options}
      selected={labelIds}
      onToggle={toggleLabelFilter}
      onClear={clearLabelFilters}
    />
  );
}
